import React, { useEffect, useState } from "react";
import axios from "axios";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { getEventsPage, getEventPDFs, sendEventAnnouncement, uploadEventPDF } from "../../api/requests";
import { ConfirmDeleteModal, LoadState, PlainText } from "../../Components/admin/AdminShared";
import EventPagination from "../../Components/Events/EventPagination";

const emptyEvent = {
    event_name: "",
    start_datetime: "",
    end_datetime: "",
    event_description: "",
    number_participants: 0,
    access_restricted: 1,
    is_active: true,
};

const accessLabels = { 1: "Öffentlich", 2: "Vereinsintern", 3: "Vorstand" };

const formatDateTime = (value) => value
    ? new Date(value).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })
    : "Nicht angegeben";

const EventManagement = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [saveError, setSaveError] = useState("");
    const [notice, setNotice] = useState("");
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [ordering, setOrdering] = useState("start_datetime");
    const [page, setPage] = useState(1);
    const [pageInfo, setPageInfo] = useState({ count: 0, next: null, previous: null });
    const [revision, setRevision] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [eventData, setEventData] = useState(emptyEvent);
    const [pdfs, setPDFs] = useState([]);
    const [pdfFile, setPDFFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [sendingAnnouncement, setSendingAnnouncement] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const token = localStorage.getItem("token");
    const auth = { headers: { Authorization: `Bearer ${token}` } };

    useEffect(() => {
        let cancelled = false;
        const loadEvents = async () => {
            setLoading(true);
            setLoadError("");
            try {
                const result = await getEventsPage({ page, search, status: statusFilter, ordering });
                if (cancelled) return;
                setEvents(result.results);
                setPageInfo(result);
            } catch (error) {
                if (cancelled) return;
                if (error?.response?.status === 404 && page > 1) {
                    setPage((current) => current - 1);
                } else {
                    setLoadError("Events konnten nicht geladen werden. Bitte versuche es erneut.");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        loadEvents();
        return () => { cancelled = true; };
    }, [page, search, statusFilter, ordering, revision, token]);

    const openModal = async (event = null) => {
        setSelectedEvent(event);
        setEventData(event ? {
            event_name: event.event_name || "",
            start_datetime: event.start_datetime?.slice(0, 16) || "",
            end_datetime: event.end_datetime?.slice(0, 16) || "",
            event_description: event.event_description || "",
            number_participants: event.number_participants ?? 0,
            access_restricted: event.access_restricted ?? 1,
            is_active: event.is_active ?? true,
        } : emptyEvent);
        setPDFFile(null);
        setPDFs([]);
        setSaveError("");
        setShowModal(true);
        if (event?.id) {
            try {
                setPDFs(await getEventPDFs(event.id));
            } catch {
                setPDFs([]);
            }
        }
    };

    const closeModal = () => {
        if (!saving && !sendingAnnouncement) setShowModal(false);
    };

    const handleSendAnnouncement = async () => {
        if (!selectedEvent?.id) return;
        setSendingAnnouncement(true);
        setSaveError("");
        try {
            const result = await sendEventAnnouncement(selectedEvent.id);
            const sentEvent = result.event || {
                ...selectedEvent,
                announcement_sent_at: result.announcement_sent_at,
            };
            setSelectedEvent(sentEvent);
            setEvents((current) => current.map((item) => item.id === sentEvent.id ? sentEvent : item));
            setNotice("EventMail wurde versendet.");
        } catch (error) {
            const status = error?.response?.status;
            if (status === 400) {
                setSaveError("Die EventMail kann nur für aktive Events mit Empfängern versendet werden.");
            } else if (status === 409) {
                setSaveError("Die EventMail wurde bereits versendet.");
            } else if (status === 403) {
                setSaveError("Du hast keine Berechtigung, EventMails zu versenden.");
            } else {
                setSaveError("Die EventMail konnte nicht versendet werden.");
            }
        } finally {
            setSendingAnnouncement(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (new Date(eventData.end_datetime) < new Date(eventData.start_datetime)) {
            setSaveError("Das Ende darf nicht vor dem Beginn liegen.");
            return;
        }
        setSaving(true);
        setSaveError("");
        const payload = {
            ...eventData,
            number_participants: Number(eventData.number_participants),
            access_restricted: Number(eventData.access_restricted),
        };
        try {
            const response = selectedEvent
                ? await axios.put(`${process.env.REACT_APP_API_URL}/events/${selectedEvent.id}/`, payload, auth)
                : await axios.post(`${process.env.REACT_APP_API_URL}/events/`, payload, auth);
            if (pdfFile) await uploadEventPDF(response.data.id, pdfFile);
            setRevision((current) => current + 1);
            setShowModal(false);
            setNotice(selectedEvent ? "Event wurde aktualisiert." : "Event wurde erstellt.");
        } catch {
            setSaveError("Das Event konnte nicht gespeichert werden. Bitte prüfe die Eingaben und den PDF-Anhang.");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/events/${deleteTarget.id}/`, auth);
            if (events.length === 1 && page > 1) setPage((current) => current - 1);
            setRevision((current) => current + 1);
            setDeleteTarget(null);
            setNotice("Event wurde gelöscht.");
        } catch {
            setNotice("");
            setLoadError("Das Event konnte nicht gelöscht werden.");
        } finally {
            setDeleting(false);
        }
    };

    const editorBusy = saving || sendingAnnouncement;
    const announcementSent = Boolean(selectedEvent?.announcement_sent_at);
    const announcementButtonTitle = !selectedEvent?.id
        ? "Event muss zuerst gespeichert werden."
        : announcementSent
            ? "EventMail wurde bereits versendet."
            : !selectedEvent.is_active
                ? "Event muss aktiv sein."
                : pdfFile
                    ? "PDF-Anhang muss zuerst gespeichert werden."
                    : "EventMail an Mitglieder versenden.";
    const canSendAnnouncement = Boolean(
        selectedEvent?.id
        && selectedEvent.is_active
        && !announcementSent
        && !pdfFile
        && !editorBusy
    );

    return (
        <section aria-labelledby="event-admin-title">
            <div className="admin-page-heading">
                <div>
                    <p className="admin-eyebrow">Termine</p>
                    <h1 id="event-admin-title">Events</h1>
                    <p>Termine, Zielgruppen und Anhänge zentral verwalten.</p>
                </div>
                <Button onClick={() => openModal()}>Neues Event</Button>
            </div>

            {notice && <Alert variant="success" dismissible onClose={() => setNotice("")}>{notice}</Alert>}
            <div className="admin-toolbar admin-toolbar--events">
                <Form.Control
                    type="search"
                    aria-label="Events durchsuchen"
                    placeholder="Name und Beschreibung durchsuchen …"
                    value={search}
                    onChange={(event) => { setSearch(event.target.value); setPage(1); }}
                />
                <Form.Select
                    aria-label="Events nach Status filtern"
                    value={statusFilter}
                    onChange={(event) => { setStatusFilter(event.target.value); setPage(1); }}
                >
                    <option value="all">Alle Status</option>
                    <option value="active">Aktiv</option>
                    <option value="inactive">Inaktiv</option>
                </Form.Select>
                <Form.Select
                    aria-label="Events sortieren"
                    value={ordering}
                    onChange={(event) => { setOrdering(event.target.value); setPage(1); }}
                >
                    <option value="start_datetime">Datum aufsteigend</option>
                    <option value="-start_datetime">Datum absteigend</option>
                </Form.Select>
            </div>

            <LoadState
                loading={loading}
                error={loadError}
                empty={events.length === 0}
                emptyText={search || statusFilter !== "all" ? "Keine Events passen zu den Filtern." : "Noch keine Events vorhanden."}
            >
                <div className="admin-list">
                    {events.map((event) => (
                        <article className="admin-item" key={event.id}>
                            <div className="admin-item__header">
                                <div>
                                    <h2 className="admin-item__title">{event.event_name}</h2>
                                    <div className="admin-item__meta">
                                        <span>{formatDateTime(event.start_datetime)} – {formatDateTime(event.end_datetime)}</span>
                                        <span>{event.number_participants} Plätze</span>
                                        <span>{accessLabels[event.access_restricted] || "Unbekannter Zugriff"}</span>
                                    </div>
                                </div>
                                <span className={`admin-status${event.is_active ? " is-positive" : ""}`}>
                                    {event.is_active ? "Aktiv" : "Inaktiv"}
                                </span>
                            </div>
                            <PlainText className="admin-item__description">{event.event_description}</PlainText>
                            <div className="admin-item__footer">
                                <span className="admin-item__meta">Start: {formatDateTime(event.start_datetime)}</span>
                                <div className="admin-actions">
                                    <Button variant="outline-secondary" onClick={() => openModal(event)}>Bearbeiten</Button>
                                    <Button variant="outline-danger" onClick={() => setDeleteTarget(event)}>Löschen</Button>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            </LoadState>
            <EventPagination page={page} {...pageInfo} loading={loading} onPageChange={setPage} />

            <Modal
                show={showModal}
                onHide={closeModal}
                centered size="lg"
                className="admin-editor-modal"
                backdropClassName="admin-modal-backdrop"
            >
                <Modal.Header closeButton={!editorBusy}>
                    <Modal.Title>{selectedEvent ? "Event bearbeiten" : "Event erstellen"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {saveError && <Alert variant="danger">{saveError}</Alert>}
                    <Form id="event-editor-form" onSubmit={handleSubmit}>
                        <Form.Group className="mb-3" controlId="event-name">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                value={eventData.event_name}
                                onChange={(event) => setEventData({ ...eventData, event_name: event.target.value })}
                                required autoFocus disabled={editorBusy}
                            />
                        </Form.Group>
                        <div className="row g-3 mb-3">
                            <Form.Group className="col-md-6" controlId="event-start">
                                <Form.Label>Beginn</Form.Label>
                                <Form.Control
                                    type="datetime-local"
                                    value={eventData.start_datetime}
                                    onChange={(event) => setEventData({ ...eventData, start_datetime: event.target.value })}
                                    required disabled={editorBusy}
                                />
                            </Form.Group>
                            <Form.Group className="col-md-6" controlId="event-end">
                                <Form.Label>Ende</Form.Label>
                                <Form.Control
                                    type="datetime-local"
                                    min={eventData.start_datetime}
                                    value={eventData.end_datetime}
                                    onChange={(event) => setEventData({ ...eventData, end_datetime: event.target.value })}
                                    required disabled={editorBusy}
                                />
                            </Form.Group>
                        </div>
                        <Form.Group className="mb-3" controlId="event-description">
                            <Form.Label>Beschreibung</Form.Label>
                            <Form.Control
                                as="textarea" rows={8}
                                value={eventData.event_description}
                                onChange={(event) => setEventData({ ...eventData, event_description: event.target.value })}
                                required disabled={editorBusy}
                            />
                            <Form.Text>Zeilenumbrüche und leere Absätze bleiben in der Eventansicht erhalten.</Form.Text>
                        </Form.Group>
                        <div className="row g-3 mb-3">
                            <Form.Group className="col-md-6" controlId="event-capacity">
                                <Form.Label>Verfügbare Plätze</Form.Label>
                                <Form.Control
                                    type="number" min="0"
                                    value={eventData.number_participants}
                                    onChange={(event) => setEventData({ ...eventData, number_participants: event.target.value })}
                                    required disabled={editorBusy}
                                />
                            </Form.Group>
                            <Form.Group className="col-md-6" controlId="event-access">
                                <Form.Label>Zugriff</Form.Label>
                                <Form.Select
                                    value={eventData.access_restricted}
                                    onChange={(event) => setEventData({ ...eventData, access_restricted: Number(event.target.value) })}
                                    disabled={editorBusy}
                                >
                                    <option value={1}>Öffentlich</option>
                                    <option value={2}>Vereinsintern</option>
                                    <option value={3}>Vorstand</option>
                                </Form.Select>
                            </Form.Group>
                        </div>
                        <Form.Group className="mb-3">
                            <Form.Check
                                id="event-active"
                                type="switch" label="Event ist aktiv"
                                checked={eventData.is_active}
                                onChange={(event) => setEventData({ ...eventData, is_active: event.target.checked })}
                                disabled={editorBusy}
                            />
                        </Form.Group>
                        <Form.Group controlId="event-pdf">
                            <Form.Label>PDF-Anhang <span className="text-muted">(optional)</span></Form.Label>
                            <Form.Control
                                type="file" accept="application/pdf"
                                onChange={(event) => setPDFFile(event.target.files[0] || null)}
                                disabled={editorBusy}
                            />
                            {pdfs.length > 0 && (
                                <ul className="mt-2 mb-0">
                                    {pdfs.map((pdf) => (
                                        <li key={pdf.id}>
                                            <a href={pdf.file} target="_blank" rel="noopener noreferrer">{pdf.file.split("/").pop()}</a>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    {selectedEvent && (
                        <Button
                            variant="outline-primary"
                            onClick={handleSendAnnouncement}
                            disabled={!canSendAnnouncement}
                            title={announcementButtonTitle}
                        >
                            {sendingAnnouncement && <Spinner animation="border" size="sm" className="me-2" />}
                            EventMail versenden
                        </Button>
                    )}
                    <Button variant="light" onClick={closeModal} disabled={editorBusy}>Abbrechen</Button>
                    <Button type="submit" form="event-editor-form" disabled={editorBusy}>
                        {saving && <Spinner animation="border" size="sm" className="me-2" />}
                        Speichern
                    </Button>
                </Modal.Footer>
            </Modal>

            <ConfirmDeleteModal
                show={Boolean(deleteTarget)}
                title={deleteTarget?.event_name || ""}
                busy={deleting}
                onCancel={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
            />
        </section>
    );
};

export default EventManagement;
